import { useFormContext, useWatch } from "react-hook-form"; // Updated imports
import { useEffect, useState } from "react";
import SelectField from "../../form-fields/SelectField";
import InputField from "../../form-fields/InputField";
import apiHelper from "../../../utils/apiHelper";
import { PaymentService } from "@propertelligent/client-api";
import { UserLoginData } from "../../../components/common/charts/Enums";

// Form values type for context
type FormValues = {
  mode: string;
  date: string;
  checkNo?: string;
  transactionId?: string;
};

// Export the PaymentMethod type so the parent can use it
export type PaymentMethod = {
  Id: string;
  Name: string;
  PaymentMethodTypeId: string;
};

export type PaymentPanelProps = {
  paidAmount: number;
  defaultMode?: string;
  defaultDate?: string;
  setPaymentMethods: (methods: PaymentMethod[]) => void; // Prop to lift state up
};

export default function PaymentPanel({
  paidAmount,
  defaultDate = "12/02/2025",
  setPaymentMethods,
}: PaymentPanelProps) {
  const fmt = (n: number) => `$${n.toFixed(2)}`;

  // Get form methods from the parent's FormProvider
  const { control, resetField, setValue } = useFormContext<FormValues>();

  const [localPaymentMethods, setLocalPaymentMethods] = useState<PaymentMethod[]>([]);
  const selectedMode = useWatch({ control, name: "mode" });
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await apiHelper(
          PaymentService.getApiPaymentMethodOrganizationlist,
          { showNotification: false },
          orgId
        );

        if (response.data?.success) {
          const methods: PaymentMethod[] = response.data.success.map((item: any) => ({
            Id: item.id.toString(),
            Name: item.accountName,
            PaymentMethodTypeId: item.formatted.PaymentMethodTypeId,
          }));
          setLocalPaymentMethods(methods); // Set local state for rendering
          setPaymentMethods(methods); // Lift state up to parent
          // Set the default form value once methods are loaded
          if (methods.length > 0 && !control._formValues.mode) {
             setValue("mode", methods[0].Id);
          }
        }
      } catch (error) {
        console.error("Failed to fetch payment methods:", error);
      }
    };

    fetchPaymentMethods();
  }, [orgId, setPaymentMethods, setValue, control]);

  useEffect(() => {
    // Reset conditional fields when payment method changes
    resetField("checkNo");
    resetField("transactionId");
  }, [selectedMode, resetField]);

  const isCheck = selectedMode && localPaymentMethods.find((m) => m.Id === selectedMode)?.PaymentMethodTypeId === "Check";
  const isACH = selectedMode && localPaymentMethods.find((m) => m.Id === selectedMode)?.PaymentMethodTypeId === "ACH";

  return (
    <aside className="panel right-panel" aria-labelledby="summary-heading">
      <header className="panel-header">
        <h2 id="summary-heading" className="panel-title">
          Payment Summary
        </h2>
      </header>

      <div className="paid-banner" aria-label="You've Paid">
        <span className="paid-label">You’ve Paid</span>
        <span className="paid-dots" aria-hidden="true" />
        <span className="paid-amount">{fmt(paidAmount)}</span>
      </div>

      {/* No <form> tag needed here anymore */}
      <div className="summary-section">
        <h3 className="section-title">Choose Payment Method</h3>

        <SelectField
          name="mode"
          label="Payment Mode"
          required={true}
          requiredMessage="Please select a payment method"
          options={localPaymentMethods}
          placeholderText="Select a payment method"
        />

        <div className="field">
          <InputField
            name="date"
            label="Payment Date"
            type="date"
            required={true}
            requiredMessage="Please enter a payment date"
            placeholder="MM/DD/YYYY"
          />
        </div>

        {isCheck && (
          <InputField
            name="checkNo"
            label="Enter Check No"
            type="text"
            required={true}
            requiredMessage="Please enter a check number"
            placeholder="Enter check number"
            onlyNumeric={true}
          />
        )}

        {isACH && (
          <InputField
            name="transactionId"
            label="Transaction ID"
            type="text"
            required={true}
            requiredMessage="Please enter a transaction ID"
            placeholder="Enter transaction ID"
          />
        )}
      </div>
    </aside>
  );
}